import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import type { Mesh } from '@babylonjs/core/Meshes/mesh'
import type { Scene } from '@babylonjs/core/scene'
import type { AssetContainer, InstantiatedEntries } from '@babylonjs/core/assetContainer'
import type { PlayerState } from '../contracts/game'
import type { MaterialLibrary } from '../rendering/MaterialLibrary'
import type { LocalProfile } from '../persistence/ProfileRepository'
import { COSMETICS, type ShellId } from '../content/CosmeticCatalog'

export class PlayerView {
  private readonly root: TransformNode
  private readonly shell: Mesh
  private readonly rings: readonly Mesh[]
  private readonly trail: Mesh
  private readonly fins: readonly Mesh[]
  private readonly nodes: readonly Mesh[]
  private readonly debugCollider: Mesh
  private readonly materials: MaterialLibrary
  private modelEntries: InstantiatedEntries | null = null
  private rollX = 0
  private rollZ = 0
  private trailWidth = 1
  private activeShell: ShellId = 'ion-core'

  public constructor(scene: Scene, materials: MaterialLibrary) {
    this.materials = materials
    this.root = new TransformNode('hero-root', scene)
    this.shell = MeshBuilder.CreateIcoSphere('hero-shell', { radius: 1, subdivisions: 5, flat: false }, scene)
    this.shell.material = materials.hero
    this.shell.parent = this.root
    this.rings = [0, 1, 2].map((index) => {
      const ring = MeshBuilder.CreateTorus(`hero-ring-${index}`, {
        diameter: 2.05,
        thickness: 0.035,
        tessellation: 40,
      }, scene)
      ring.material = materials.edge
      ring.parent = this.root
      ring.rotation.x = index * Math.PI / 3
      ring.rotation.z = index * Math.PI / 4
      return ring
    })
    this.trail = MeshBuilder.CreateCylinder('hero-trail', { height: 1, diameterTop: 0.08, diameterBottom: 0.8, tessellation: 10 }, scene)
    this.trail.material = materials.trail
    this.trail.parent = this.root
    this.trail.rotation.x = Math.PI / 2
    this.fins = Array.from({ length: 4 }, (_, index) => {
      const fin = MeshBuilder.CreateBox(`hero-fin-${index}`, { size: 1 }, scene)
      const angle = index * Math.PI / 2
      fin.material = materials.hero
      fin.parent = this.root
      fin.position.set(Math.cos(angle) * 1.03, Math.sin(angle) * 1.03, 0.08)
      fin.scaling.set(0.18, 0.42, 0.82)
      fin.rotation.z = angle
      fin.rotation.y = angle * 0.18
      fin.setEnabled(false)
      return fin
    })
    this.nodes = Array.from({ length: 6 }, (_, index) => {
      const node = MeshBuilder.CreatePolyhedron(`hero-orbit-node-${index}`, { type: index % 3, size: 0.18 }, scene)
      node.material = materials.edge
      node.parent = this.root
      node.setEnabled(false)
      return node
    })
    this.debugCollider = MeshBuilder.CreateIcoSphere('hero-collider-debug', { radius: 1, subdivisions: 2 }, scene)
    this.debugCollider.material = materials.debug
    this.debugCollider.parent = this.root
    this.debugCollider.isPickable = false
    this.debugCollider.setEnabled(false)
  }

  public applyProfile(profile: LocalProfile): void {
    this.activeShell = profile.shell
    const shellIndex = Math.max(0, COSMETICS.shells.findIndex((entry) => entry.id === profile.shell))
    const visibleRings = profile.shell === 'ion-core' ? 3
      : ['reactor-cage', 'helix', 'sentinel', 'apex'].includes(profile.shell) ? 3
        : profile.shell === 'ghost-lattice' ? 2 : 1
    for (let index = 0; index < this.rings.length; index += 1) {
      const ring = this.rings[index]!
      const scale = 0.96 + (shellIndex % 4) * 0.025 + index * 0.025
      ring.scaling.setAll(scale)
      ring.rotation.x = index * Math.PI / 3 + shellIndex * 0.11
      ring.rotation.z = index * Math.PI / 4 - shellIndex * 0.07
    }
    this.shell.scaling.setAll(0.985 + shellIndex % 3 * 0.008)
    const trailIndex = Math.max(0, COSMETICS.trails.findIndex((entry) => entry.id === profile.trail))
    this.trailWidth = 0.72 + trailIndex * 0.07
    this.materials.setHeroSkin(profile.shell)
    this.materials.setTrailSkin(profile.trail)
    this.applyShellGeometry(visibleRings)
  }

  public applyAssetContainer(container: AssetContainer): void {
    this.modelEntries?.rootNodes.forEach((node) => node.dispose())
    const entries = container.instantiateModelsToScene((name) => `hero-active-${name}`, false)
    for (const node of entries.rootNodes) {
      node.parent = this.root
      for (const mesh of node.getChildMeshes(false)) {
        mesh.material = /VIS_HERO_(ENERGY|CHANNEL)/i.test(mesh.name) ? this.materials.edge : this.materials.hero
        mesh.isPickable = false
      }
    }
    this.modelEntries = entries
    this.shell.setEnabled(false)
    this.applyShellGeometry()
  }

  public update(player: Readonly<PlayerState>, alpha: number, renderOriginZ: number, speedNormalized: number, reducedMotion: boolean): void {
    const x = player.previousPosition.x + (player.position.x - player.previousPosition.x) * alpha
    const y = player.previousPosition.y + (player.position.y - player.previousPosition.y) * alpha
    const z = player.previousPosition.z + (player.position.z - player.previousPosition.z) * alpha - renderOriginZ
    this.root.position.set(x, y, z)
    this.rollX += player.forwardVelocity / 120
    this.rollZ -= player.lateralVelocity / 120
    this.shell.rotation.set(this.rollX, 0, this.rollZ)
    this.root.rotation.set(this.rollX * 0.14, this.rollZ * 0.22, this.rollZ * 0.1)
    for (let index = 0; index < this.rings.length; index += 1) {
      this.rings[index]!.rotation.y += (0.006 + speedNormalized * 0.02) * (index + 1)
    }
    for (let index = 0; index < this.nodes.length; index += 1) {
      const angle = this.rollX * 0.035 + index / this.nodes.length * Math.PI * 2
      this.nodes[index]!.position.set(Math.cos(angle) * 1.22, Math.sin(angle) * 1.22, Math.sin(angle * 2) * 0.22)
      this.nodes[index]!.rotation.y += 0.025 + index * 0.002
    }
    const trailLength = reducedMotion ? 0.9 : 1.2 + speedNormalized * 4.8
    this.trail.position.set(0, 0, trailLength * -0.5 - 0.8)
    this.trail.scaling.set(this.trailWidth, trailLength, this.trailWidth)
    this.trail.visibility = 0.3 + speedNormalized * 0.45
    this.trail.setEnabled(player.alive)
    this.root.setEnabled(player.alive)
  }

  public updateMenu(timeSeconds: number): void {
    this.root.setEnabled(true)
    this.root.position.set(5.7, 2.15 + Math.sin(timeSeconds * 0.75) * 0.14, 8.5)
    this.root.rotation.set(timeSeconds * 0.18, timeSeconds * 0.27, Math.sin(timeSeconds * 0.4) * 0.12)
    this.shell.rotation.set(timeSeconds * 0.24, timeSeconds * 0.13, timeSeconds * -0.19)
    this.trail.setEnabled(false)
  }

  public setVisible(visible: boolean): void {
    this.root.setEnabled(visible)
  }

  public setColliderDebug(enabled: boolean): void {
    this.debugCollider.setEnabled(enabled)
  }

  private applyShellGeometry(fallbackRingCount = 3): void {
    const hasExternalRings = ['reactor-cage', 'ghost-lattice', 'helix', 'sentinel', 'apex'].includes(this.activeShell)
    const ringCount = this.modelEntries === null ? fallbackRingCount : hasExternalRings ? 3 : 0
    for (let index = 0; index < this.rings.length; index += 1) this.rings[index]!.setEnabled(index < ringCount)
    const hasFins = ['facet', 'vector', 'eclipse', 'rift', 'sentinel', 'apex'].includes(this.activeShell)
    for (const fin of this.fins) fin.setEnabled(hasFins)
    const hasNodes = ['nova-prism', 'ghost-lattice', 'pulse', 'rift', 'apex'].includes(this.activeShell)
    for (const node of this.nodes) node.setEnabled(hasNodes)
  }
}
